import styled from "styled-components";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useHistory, useLocation} from "react-router-dom";
import {useNoWindowScroll} from "../utils/use_no_window_scroll";

const REGISTRY = {
    CreateComment: import("./create_comment"),
    CreateMessage: import("./create_message"),
    CreateReply: import("./create_reply"),
    CreateModMessage: import("./create_modmessage"),
    EditComment: import("./edit_comment"),
    EditPost: import("./edit_post"),
    ImageView: import("./image_view"),
    Recaptcha: import("./recaptcha"),
    Sidebar: import("./sidebar"),
    CommunityOnboard: import("./community_onboard"),
};

async function resolve(name) {
    const promise = REGISTRY[name];
    if (!promise) {
        throw new Error(`unknown modal ${name}`);
    }
    const module = await promise;
    if (typeof module === 'object' && name in module) {
        // console.log(`found ${name} in module`, module[name]);
        return module[name];
    }
    if (typeof module === 'object' && 'default' in module) {
        if (typeof module.default === 'object' && name in module.default) {
            // console.log(`found ${name} in default`, module.default[name]);
            return module.default[name];
        }
        // console.log(`found ${name} as default`);
        return module.default;
    }
    throw new Error(`failed to load modal ${name}`);
}

function assertNameValid(name) {
    if (typeof name !== 'string') {
        throw new Error('modal name must be a string');
    }
    if (!(name in REGISTRY)) {
        throw new Error(`no resolver for modal ${name}`);
    }
}

function assertPropsValid(obj) {
    for (const [key, value] of Object.entries(obj)) {
        if (typeof value === 'function') {
            throw new Error(`bad modal prop ${key}`);
        }
    }
}

const modalClosed = (layers, n) =>
    layers.slice(0, layers.length - n);

const modalOpened = (layers, id, name, props) =>
    [...layers, { id, name, props }];

const modalReplaced = (layers, id, name, props) =>
    modalOpened(modalClosed(layers, 1), id, name, props)

const ModalRoot = styled.div`
  > * {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    overflow: hidden;
    z-index: 10;
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  > .hidden {
    visibility: hidden;
  }
`;

function ModalLayer({ api, id, depth, name, props }) {
    const [Component, setComponent] = useState(null);
    const postMessage = useCallback(
        (message) => api.postMessage(id, message),
        [api, id]
    );
    const close = useCallback(
        () => api.close(depth + 1),
        [api, depth]
    );
    useEffect(() => {
        resolve(name)
            .then((Component) => {
                setComponent(() => Component);
            })
            .catch((error) => {
                if (import.meta.env.DEV) {
                    // eslint-disable-next-line no-console
                    console.log(error);
                }
                close();
            });
    }, [name, setComponent, close]);
    return (
        <div className={depth === 0 ? 'visible' : 'hidden'}>
            {Boolean(Component) && (
                <Component onMessage={postMessage} onClose={close} {...props} />
            )}
        </div>
    );
}

export function ModalContainer({layers, api}) {
    if (layers.length === 0) {
        return false;
    }
    return (
        <ModalRoot>
            {layers.map(({id, name, props}, index) => {
                return (
                    <ModalLayer
                        key={id}
                        id={id}
                        api={api}
                        depth={layers.length - 1 - index}
                        name={name}
                        props={props}
                    />
                );
            })}
        </ModalRoot>
    );
}
ModalContainer.Root = ModalRoot;

let nextId = 1;
function getNextId() {
    const id = nextId.toString();
    nextId += 1;
    return id;
}

export function useModalStack(registry) {
    const history = useHistory();
    const location = useLocation();
    const subscriptions = useRef({});
    const api = useMemo(() => {
        return ({
            close(n = 1) {
                // TODO: clean up subscriptions for all modals closed
                const layers = modalClosed(location.state?.layers || [], n );
                if (location.state?.layers && layers.length === 0) {
                    history.go(-1);
                } else {
                    history.replace({ ...location, state: { layers } });
                }
            },
            open(name, props = {}) {
                assertNameValid(name);
                assertPropsValid(props);
                const id = getNextId();
                subscriptions.current[id] = [];
                const layers = location.state?.layers || [];
                if (layers.length === 0) {
                    history.push({
                        ...location,
                        state: { layers: modalOpened(layers, id, name, props ) },
                    });
                } else {
                    history.replace({
                        ...location,
                        state: { layers: modalReplaced(layers, id, name, props ) },
                    });
                }
                return {subscribe: (callback) => api.subscribe(id, callback)};
            },
            push(name, props = {}) {
                assertNameValid(name);
                assertPropsValid(props);
                const id = getNextId();
                subscriptions.current[id] = [];
                history.push({
                    ...location,
                    state: { layers: modalOpened(location.state?.layers || [], id, name, props ) },
                });
                return {subscribe: (callback) => api.subscribe(id, callback)};
            },
            subscribe: (id, callback) => {
                subscriptions.current[id].push(callback);
                return () => {
                    const index = subscriptions.current.indexOf(callback);
                    if (index !== -1) {
                        subscriptions.current.splice(index, 1);
                    }
                };
            },
            postMessage: (id, message) => {
                for (const func of subscriptions.current[id] || []) {
                    func(message);
                }
            },
        });
    }, [history, location]);
    const layers = location.state?.layers || [];
    useNoWindowScroll(layers.length > 0);
    return [<ModalContainer registry={registry} layers={layers} api={api}/>, api];
}
